import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import {
  SignUpPage,
  ConsentPage,
  LoginPage,
  ReferralLinkPage,
  VerifyOtpPage,
  SsoCallbackPage,
} from "../features/auth";
import {
  AddClientPage,
  ClientDetailPage,
  EditClientPage,
  MyClientsPage,
} from "../features/clients";
import { DashboardPage } from "../features/dashboard";
import { MyTaskPage, NewTaskPage } from "../features/myTask";
import { MyBrandPage } from "../features/brand";
import { CreatePlanPage, PlanDetailPage } from "../features/mealPlans";
import { CreateSupplementPage } from "../features/supplements";
import { DishesPage } from "../features/dishes";
import { MeetingProviderCallbackPage } from "../features/appointments";
import {
  AddEnquiryPage,
  AllEnquiriesPage,
  EditEnquiryPage,
  EnquiryDetailPage,
} from "../features/enquiries";
import { RevenuePage } from "../features/revenue";
import { LaunchAppPage } from "../features/launch";
import { CreatePagePage } from "../features/page";
import {
  BillingCheckoutPage,
  SubscriptionResultPage,
  TrialDetailsPage,
  TrialPlanPage,
  TrialStartedPage,
} from "../features/billing";
import { BookCallPage, SettingsPage } from "../features/settings";
import AppLayout from "../components/layout/AppLayout";
import { OfferTrialFirst, RequireAuth, RedirectIfSignedIn } from "./guards";
import DocumentTitle from "./documentTitle";
import RouteTrace from "./routeTrace";
import { BILLING_SETTINGS_PATH, ROUTES } from "./paths";

/* There used to be a `PLACEHOLDERS` list here, mapping the sidebar
   destinations with no screen yet onto a stand-in page. Every rail item leads
   to a real screen now — Create page and Launch app were the last two — so the
   list and its map are gone rather than left empty. `PlaceholderPage` itself
   stays where it is for the next unbuilt destination. */

/**
 * Route table for the app. Auth screens render standalone and are closed to
 * anyone already signed in; everything behind RequireAuth renders inside
 * AppLayout, which supplies the sidebar.
 */
const AppRoutes: React.FC = () => (
  <>
    <DocumentTitle />
    <RouteTrace />
    <Routes>
      <Route path="/" element={<Navigate to={ROUTES.login} replace />} />

      <Route element={<RedirectIfSignedIn />}>
        <Route path={ROUTES.signup} element={<SignUpPage />} />
        <Route path={ROUTES.consent} element={<ConsentPage />} />
        <Route path={ROUTES.login} element={<LoginPage />} />
        <Route path={ROUTES.verifyOtp} element={<VerifyOtpPage />} />
      </Route>

      {/* A partner's referral link, which lands on the sign-up form: somebody
          following an invitation does not have an account yet.

          Outside `RedirectIfSignedIn` on purpose: the code has to be parked
          before anything decides where this visitor goes, and this path's own
          answer is to replace itself with /signup, which is inside the guard
          and gets that decision made properly.

          Neither of these can shadow the screen it sits under: both are a
          segment longer, so `/signup` and `/login` still match themselves. */}
      <Route
        path={ROUTES.signupReferral}
        element={<ReferralLinkPage to={ROUTES.signup} />}
      />
      {/* The same link in the shape the backend used to issue, still honoured
          for every one already printed or sent. */}
      <Route
        path={ROUTES.loginReferral}
        element={<ReferralLinkPage to={ROUTES.login} />}
      />

      {/* Outside both guards: this is the screen that turns the Google redirect
          into a session, so it has to run while signed out and must not be
          redirected away the moment it succeeds. */}
      <Route path={ROUTES.ssoCallback} element={<SsoCallbackPage />} />

      <Route element={<RequireAuth />}>
        {/* Registered so the consent gate's own path matches inside this group.
            `RequireAuth` holds the handshake's state - including the promise the
            gate resolves - and a path outside the group would unmount it
            mid-flight. It answers for this path in every phase itself, so the
            element below only ever stands in as a belt-and-braces redirect.
            Outside `AppLayout` on purpose: the gate is not a screen of the app,
            and does not belong inside its sidebar. */}
        <Route
          path={ROUTES.consentRequired}
          element={<Navigate to={ROUTES.dashboard} replace />}
        />

        {/* The free trial a new account is offered, and the only screens
            behind this guard that are outside `AppLayout` on purpose: the
            sidebar appears for the first time on the dashboard the last of
            them leads to. Signed in, because the detail step records the
            billing profile against the session.

            Ordered longest first, the way `addClient` sits above the client
            detail route: the router ranks the more specific path first either
            way, and the order here says so on the page. */}
        <Route path={ROUTES.trialStarted} element={<TrialStartedPage />} />
        <Route path={ROUTES.trialDetails} element={<TrialDetailsPage />} />
        <Route path={ROUTES.trial} element={<TrialPlanPage />} />

        {/* Above `AppLayout`, not inside it: a workspace that can still take
            the free trial is sent to the offer before any of the app is drawn,
            sidebar and all. It asks on the dashboard's path alone and lets
            every other screen through untouched — see `OfferTrialFirst`. */}
        <Route element={<OfferTrialFirst />}>
          <Route element={<AppLayout />}>
            <Route path={ROUTES.dashboard} element={<DashboardPage />} />
            {/* Ahead of the day it sits under, the way `addClient` is ahead
                of the client detail route: the router ranks the longer path
                first either way, and the order here says so on the page. */}
            <Route path={ROUTES.newTask} element={<NewTaskPage />} />
            <Route path={ROUTES.myTask} element={<MyTaskPage />} />
            <Route path={ROUTES.myClients} element={<MyClientsPage />} />
            <Route path={ROUTES.addClient} element={<AddClientPage />} />
            {/* Ahead of the detail route it sits under, the way `addClient` is
                ahead of both: the router ranks the longer, more specific path
                first either way, and the order here says so on the page. */}
            <Route path={ROUTES.editClient} element={<EditClientPage />} />
            <Route path={ROUTES.myClientDetail} element={<ClientDetailPage />} />
            <Route path={ROUTES.myBrand} element={<MyBrandPage />} />
            <Route path={ROUTES.createPlan} element={<CreatePlanPage />} />
            <Route path={ROUTES.planDetail} element={<PlanDetailPage />} />
            <Route
              path={ROUTES.createSupplement}
              element={<CreateSupplementPage />}
            />
            {/* Where Google and Zoom send the browser back to once an
                account has been linked. Inside the shell and behind the
                guard: the page finishes the job with a call of its own, which
                needs the session.

                One component, handed which app it is answering for rather
                than reading it off the path — see `ROUTES.zoomCallback`. */}
            <Route
              path={ROUTES.googleMeetCallback}
              element={<MeetingProviderCallbackPage app="google-meet" />}
            />
            <Route
              path={ROUTES.zoomCallback}
              element={<MeetingProviderCallbackPage app="zoom" />}
            />
            <Route path={ROUTES.dishes} element={<DishesPage />} />
            <Route path={ROUTES.enquiries} element={<AllEnquiriesPage />} />
            <Route path={ROUTES.addEnquiry} element={<AddEnquiryPage />} />
            {/* Ahead of the detail route it sits under, the way `addEnquiry` is
                ahead of both: the router ranks the longer, more specific path
                first either way, and the order here says so on the page. */}
            <Route path={ROUTES.editEnquiry} element={<EditEnquiryPage />} />
            <Route path={ROUTES.enquiryDetail} element={<EnquiryDetailPage />} />
            <Route path={ROUTES.revenue} element={<RevenuePage />} />
            <Route path={ROUTES.launchApp} element={<LaunchAppPage />} />
            <Route path={ROUTES.createPage} element={<CreatePagePage />} />
            {/* Ahead of the plans page it sits under, the way `addClient` is
                ahead of the client detail route: the router ranks the longer,
                more specific path first either way, and the order here says so
                on the page. The result is longer again, so it comes first. */}
            <Route
              path={ROUTES.billingResult}
              element={<SubscriptionResultPage />}
            />
            <Route
              path={ROUTES.billingCheckout}
              element={<BillingCheckoutPage />}
            />
            {/* Billing is a tab of Settings now, not a screen. The path stays
                because it is in browser histories and bookmarks — and because it
                is still the parent of the checkout above. */}
            <Route
              path={ROUTES.billing}
              element={<Navigate to={BILLING_SETTINGS_PATH} replace />}
            />
            {/* Ahead of the Settings screen it sits under, the way `addClient`
                is ahead of the client detail route: the router ranks the longer,
                more specific path first either way, and the order here says so
                on the page. */}
            <Route path={ROUTES.bookCall} element={<BookCallPage />} />
            <Route path={ROUTES.settings} element={<SettingsPage />} />
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<Navigate to={ROUTES.login} replace />} />
    </Routes>
  </>
);

export default AppRoutes;
